import React, { useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { catalogSongMessages } from '../../song/catalogSongMessages';
import { isIsrcAcceptable } from '../../song/IsrcField';
import { addSongFlowMessages } from '../addSongFlowMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ISRC_INPUT_ID = 'add-song-isrc';
const ISRC_NOTE_ID = 'add-song-isrc-note';

interface WizardIsrcFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  /** Shows the error also before the field lost the focus (Next was pressed). */
  forceError: boolean;
}

/**
 * The ISRC of step 3 (design catalog-add-song b3). It says what the code is, with an example
 * (QA 2026-10-28, lit-5 and lit-14), and gives a phone keyboard in capitals with no autocorrect
 * (mob-20). The song page keeps its own field (`IsrcField`); the rule is the same.
 */
const WizardIsrcFieldComponent: React.FC<WizardIsrcFieldProps> = ({ value, onChange, disabled, forceError }) => {
  const intl = useIntl();
  const [isTouched, setTouched] = useState(false);
  const showError = (isTouched || forceError) && !isIsrcAcceptable(value);
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={ISRC_INPUT_ID}>
        {intl.formatMessage(catalogSongMessages.isrc)}{' '}
        <span className={styles.optional}>· {intl.formatMessage(addSongFlowMessages.optional)}</span>
      </label>
      <input
        id={ISRC_INPUT_ID}
        className={clsx(styles.input, styles.inputCode, showError && styles.inputBad)}
        value={value}
        disabled={disabled}
        aria-invalid={showError}
        aria-describedby={ISRC_NOTE_ID}
        placeholder="GBABC2600042"
        maxLength={20}
        autoCapitalize="characters"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => onChange(event.target.value.toUpperCase())}
        onBlur={() => setTouched(true)}
      />
      {showError ? (
        <span className={styles.error} role="alert" id={ISRC_NOTE_ID}>
          {intl.formatMessage(catalogSongMessages.isrcError)} {intl.formatMessage(addSongFlowMessages.isrcErrorExample)}
        </span>
      ) : (
        <span className={styles.hint} id={ISRC_NOTE_ID}>
          {intl.formatMessage(addSongFlowMessages.isrcHint)}
        </span>
      )}
    </div>
  );
};

export const WizardIsrcField = TypedMemo(WizardIsrcFieldComponent);
